import React, { Component } from "react";
import Helmet from "react-helmet";
import axios from "axios";
import GridLoader from "react-spinners/GridLoader";
import Layout from "../../Layout/layout";
import { apiHost } from "../../api/apiClient";
import "../../styles/assets/nonTrackedAssets.scss";

const getValue = (obj, key) => {
  if (!obj) return null;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  return obj[pascal];
};

const emptyForm = (customerId = "") => ({ customerId, registration: "", friendlyName: "", notes: "" });

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-AU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(parsed);
};

class NonTrackedAssets extends Component {
  constructor(props) {
    super(props);
    const query = new URLSearchParams(props.location ? props.location.search : "");
    this.state = {
      assets: [],
      customers: [],
      customerFilter: query.get("customerId") || "all",
      search: "",
      isLoading: true,
      isSaving: false,
      error: "",
      message: "",
      modalOpen: false,
      editingId: null,
      form: emptyForm(query.get("customerId") || ""),
    };
  }

  componentDidMount() {
    this.loadPage();
  }

  isSystemAdmin = () => String(localStorage.getItem("role") || "")
    .replace(/\s+/g, "")
    .toLowerCase() === "systemadmin";

  canFullyManage = () => ["systemadmin", "vtdemo", "communitymanager"].includes(
    String(localStorage.getItem("role") || "").replace(/\s+/g, "").toLowerCase()
  );

  authConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  loadPage = async () => {
    this.setState({ isLoading: true, error: "" });
    try {
      const requests = [axios.get(`${apiHost}/api/Assets/NonTrackedAssets`, this.authConfig())];
      if (this.isSystemAdmin()) {
        requests.push(axios.get(`${apiHost}/api/Auth/AllCustomer`, this.authConfig()));
      }
      const responses = await Promise.all(requests);
      const assets = responses[0].data || [];
      const customers = responses[1] ? (responses[1].data || []) : [];
      const validFilter = this.state.customerFilter === "all"
        || assets.some((asset) => getValue(asset, "customerId") === this.state.customerFilter)
        || customers.some((customer) => this.customerId(customer) === this.state.customerFilter);
      this.setState({
        assets,
        customers,
        customerFilter: validFilter ? this.state.customerFilter : "all",
        isLoading: false,
      });
    } catch (error) {
      if (error.response && error.response.status === 401) {
        this.props.history.push("/login");
        return;
      }
      this.setState({ isLoading: false, error: this.errorMessage(error) });
    }
  };

  errorMessage = (error) => {
    const data = error && error.response ? error.response.data : null;
    return data && data.message ? data.message : "Unable to complete the request.";
  };

  customerId = (customer) => getValue(customer, "id") || getValue(customer, "customerId") || "";

  customerName = (customer) => getValue(customer, "name") || getValue(customer, "customerName") || "Unknown customer";

  sortedCustomers = () => [...this.state.customers]
    .sort((a, b) => this.customerName(a).localeCompare(this.customerName(b)));

  setCustomerFilter = (customerFilter) => {
    this.setState({ customerFilter }, () => {
      const params = new URLSearchParams();
      if (customerFilter !== "all") params.set("customerId", customerFilter);
      this.props.history.replace(`/non-tracked-assets${params.toString() ? `?${params}` : ""}`);
    });
  };

  filteredAssets = () => {
    const search = this.state.search.trim().toLowerCase();
    return this.state.assets.filter((asset) => {
      if (this.state.customerFilter !== "all" && getValue(asset, "customerId") !== this.state.customerFilter) return false;
      if (!search) return true;
      return [
        getValue(asset, "registration"),
        getValue(asset, "friendlyName"),
        getValue(asset, "notes"),
        getValue(asset, "customerName"),
      ].join(" ").toLowerCase().includes(search);
    });
  };

  openCreate = () => {
    const selectedCustomer = this.state.customerFilter === "all" ? "" : this.state.customerFilter;
    this.setState({
      modalOpen: true,
      editingId: null,
      form: emptyForm(selectedCustomer),
      error: "",
      message: "",
    });
  };

  openEdit = (asset) => {
    this.setState({
      modalOpen: true,
      editingId: getValue(asset, "id"),
      form: {
        customerId: getValue(asset, "customerId") || "",
        registration: getValue(asset, "registration") || "",
        friendlyName: getValue(asset, "friendlyName") || "",
        notes: getValue(asset, "notes") || "",
      },
      error: "",
      message: "",
    });
  };

  closeModal = () => {
    if (!this.state.isSaving) this.setState({ modalOpen: false, error: "" });
  };

  updateForm = (name, value) => this.setState((state) => ({
    form: { ...state.form, [name]: value },
    error: "",
  }));

  save = async (event) => {
    event.preventDefault();
    const { editingId, form } = this.state;
    this.setState({ isSaving: true, error: "", message: "" });
    try {
      const url = `${apiHost}/api/Assets/NonTrackedAssets${editingId ? `/${editingId}` : ""}`;
      if (editingId) {
        await axios.put(url, form, this.authConfig());
      } else {
        await axios.post(url, form, this.authConfig());
      }
      this.setState({
        modalOpen: false,
        editingId: null,
        isSaving: false,
        message: editingId ? "Non-tracked asset updated." : "Non-tracked asset added.",
      });
      await this.loadPage();
    } catch (error) {
      this.setState({ isSaving: false, error: this.errorMessage(error) });
    }
  };

  remove = async (asset) => {
    const registration = getValue(asset, "registration") || "this asset";
    if (!window.confirm(`Delete ${registration}? Its maintenance schedule and logs will also be deleted.`)) return;
    this.setState({ error: "", message: "" });
    try {
      await axios.delete(`${apiHost}/api/Assets/NonTrackedAssets/${getValue(asset, "id")}`, this.authConfig());
      this.setState({ message: `${registration} deleted.` });
      await this.loadPage();
    } catch (error) {
      this.setState({ error: this.errorMessage(error) });
    }
  };

  renderModal = () => {
    if (!this.state.modalOpen) return null;
    const { editingId, form, isSaving } = this.state;
    return (
      <div className="non-tracked-modal-backdrop" role="presentation">
        <div className="non-tracked-modal" role="dialog" aria-modal="true" aria-labelledby="non-tracked-modal-title">
          <div className="non-tracked-modal-head">
            <div>
              <span>Non-tracked asset</span>
              <h3 id="non-tracked-modal-title">{editingId ? "Edit asset" : "Add asset"}</h3>
            </div>
            <button type="button" onClick={this.closeModal} disabled={isSaving}>Close</button>
          </div>
          {this.state.error && <div className="non-tracked-error modal-error">{this.state.error}</div>}
          <form onSubmit={this.save}>
            {this.isSystemAdmin() && this.canFullyManage() && (
              <label>
                <span>Customer</span>
                <select required value={form.customerId} onChange={(event) => this.updateForm("customerId", event.target.value)}>
                  <option value="">Select customer</option>
                  {this.sortedCustomers().map((customer) => (
                    <option key={this.customerId(customer)} value={this.customerId(customer)}>{this.customerName(customer)}</option>
                  ))}
                </select>
              </label>
            )}
            <label>
              <span>Registration / Asset ID</span>
              <input required readOnly={!this.canFullyManage()} maxLength="100" value={form.registration} onChange={(event) => this.updateForm("registration", event.target.value)} placeholder="e.g. ABC123" />
            </label>
            <label>
              <span>Friendly name</span>
              <input maxLength="255" value={form.friendlyName} onChange={(event) => this.updateForm("friendlyName", event.target.value)} placeholder="Optional description" />
            </label>
            <label>
              <span>Notes</span>
              <textarea rows="4" maxLength="2000" value={form.notes} onChange={(event) => this.updateForm("notes", event.target.value)} placeholder="Optional asset notes" />
            </label>
            <div className="non-tracked-modal-actions">
              <button type="button" className="secondary" onClick={this.closeModal} disabled={isSaving}>Cancel</button>
              <button type="submit" disabled={isSaving}>{isSaving ? "Saving..." : editingId ? "Save changes" : "Add asset"}</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  render() {
    const assets = this.filteredAssets();
    return (
      <Layout>
        <Helmet><title>Non-tracked Assets</title></Helmet>
        <div className="contentWrapper contentWrapperSmoothRelease non-tracked-assets-page">
          <div className="non-tracked-header">
            <div>
              <span className="non-tracked-eyebrow">Manage</span>
              <h2 className="mainHeading">Non-tracked Assets</h2>
              <p>Assets without VisionTrak hardware. Date-based maintenance remains available; odometer servicing does not apply.</p>
            </div>
            {this.canFullyManage() && <button type="button" onClick={this.openCreate}>Add non-tracked asset</button>}
          </div>

          <div className="non-tracked-toolbar">
            <label>
              <span>Search</span>
              <input type="search" value={this.state.search} onChange={(event) => this.setState({ search: event.target.value })} placeholder="Registration, friendly name, customer" />
            </label>
            {this.isSystemAdmin() && (
              <label>
                <span>Customer</span>
                <select value={this.state.customerFilter} onChange={(event) => this.setCustomerFilter(event.target.value)}>
                  <option value="all">All customers</option>
                  {this.sortedCustomers().map((customer) => (
                    <option key={this.customerId(customer)} value={this.customerId(customer)}>{this.customerName(customer)}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {this.state.error && !this.state.modalOpen && <div className="non-tracked-error">{this.state.error}</div>}
          {this.state.message && <div className="non-tracked-success">{this.state.message}</div>}

          <div className="non-tracked-table-panel">
            <div className="non-tracked-table-title"><strong>{assets.length.toLocaleString()} assets</strong></div>
            {this.state.isLoading ? (
              <div className="non-tracked-loading"><GridLoader size={8} color="#7c3aed" loading /><span>Loading non-tracked assets</span></div>
            ) : (
              <div className="non-tracked-table-wrap">
                <table>
                  <thead><tr><th>Asset</th><th>Customer</th><th>Notes</th><th>Updated By</th><th>Updated At</th><th>Actions</th></tr></thead>
                  <tbody>
                    {!assets.length ? (
                      <tr><td colSpan="6" className="non-tracked-empty">No non-tracked assets match the selected filters.</td></tr>
                    ) : assets.map((asset) => (
                      <tr key={getValue(asset, "id")}>
                        <td><strong>{getValue(asset, "registration")}</strong><span>{getValue(asset, "friendlyName") || "No friendly name"}</span></td>
                        <td>{getValue(asset, "customerName") || "-"}</td>
                        <td className="non-tracked-notes">{getValue(asset, "notes") || "-"}</td>
                        <td>{getValue(asset, "updatedBy") || "-"}</td>
                        <td>{formatDateTime(getValue(asset, "updatedAt"))}</td>
                        <td><div className="non-tracked-actions"><button type="button" onClick={() => this.openEdit(asset)}>Edit</button>{this.canFullyManage() && <button type="button" className="danger" onClick={() => this.remove(asset)}>Delete</button>}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {this.renderModal()}
        </div>
      </Layout>
    );
  }
}

export default NonTrackedAssets;
